import React, { useState, useEffect } from "react";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/common/PageLoader";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Leave.css";

function LeaveBalance() {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchLeaveBalance();
  }, []);

  const fetchLeaveBalance = async () => {
    try {
      const response = await api.get("/employee-leave-balance");

      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch leave balance:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to load leave balance",
      });
    } finally {
      setLoading(false);
    }
  };

  const totalLeaveBuckets = employees.reduce(
    (sum, employee) => sum + (employee.leave_data?.length || 0),
    0,
  );
  const employeesWithLeaveData = employees.filter(
    (employee) => (employee.leave_data?.length || 0) > 0,
  ).length;

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading leave balance..." /></MainLayout>;
  }

  const leaveBalanceColumns = [
    {
      header: "Sr. No.",
      render: (_, index) => index + 1,
    },
    {
      header: "Employee Name",
      render: (employee) => (
        <div className="employee-info-cell">
          <div className="employee-avatar-small">
            {employee.first_name.charAt(0).toUpperCase()}
          </div>
          <div className="employee-details">
            <span className="employee-name-text">{employee.full_name}</span>
            <span className="employee-email">{employee.email}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Employee Code",
      render: (employee) => (
        <span className="badge-code">{employee.code}</span>
      ),
    },
    {
      header: "Leave Data",
      render: (employee) =>
        employee.leave_data.length === 0 ? (
          <p className="no-leave-data">No leave data available</p>
        ) : (
          <div className="leave-data-container">
            {employee.leave_data.map((leave, idx) => (
              <div key={idx} className="leave-item">
                <span className="leave-type-name">{leave.name}</span>
                <div className="leave-stats">
                  <span className="leave-stat total">Total: {leave.total_days}</span>
                  <span className="leave-stat-separator">|</span>
                  <span className="leave-stat used">Used: {leave.days_used || 0}</span>
                  <span className="leave-stat-separator">|</span>
                  <span className="leave-stat left">
                    Remaining: {leave.days_left}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ),
    },
  ];

  return (
    <MainLayout>
      <div className="leave-balance-container">
        <div className="balance-card">
          <div className="card-header-custom">
            <div className="header-empty">
              <span className="leave-guidance-label">Balance Desk</span>
              <h3>Employee Leave Balance</h3>
              <p className="leave-applications-subtitle">
                View current leave availability for each employee and track remaining balances by leave type.
              </p>
            </div>
          </div>

          <div className="leave-guidance-banner">
            <div>
              <span className="leave-guidance-label">Balance Snapshot</span>
              <strong>
                {employees.length
                  ? `${employees.length} employee${employees.length > 1 ? "s" : ""} loaded`
                  : "No employee leave balances available"}
              </strong>
              <p>
                Use this view to spot low balances quickly and verify that approved leave is flowing correctly into employee records.
              </p>
            </div>
            <div className="leave-summary-chips">
              <span className="leave-summary-chip">
                With leave data {employeesWithLeaveData}
              </span>
              <span className="leave-summary-chip">
                Leave buckets {totalLeaveBuckets}
              </span>
            </div>
          </div>

          <div className="card-body-custom">
            <CommonTable
              columns={leaveBalanceColumns}
              data={employees}
              tableClassName="balance-table-clean"
              wrapperClassName="balance-table-wrap"
              noDataClassName="text-center py-5 no-data"
              emptyMessage="No employees found"
              searchPlaceholder="Search by name, code or email..."
              getSearchText={(employee) =>
                [employee.full_name, employee.code, employee.email]
                  .filter(Boolean)
                  .join(" ")
              }
            />
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default LeaveBalance;
